import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Popover,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import EditImage from "../../assets/images/editIcon.svg";
import { showToast } from "../common/toastNotification";
import DeleteIcon from "../../assets/images/deleteIconRed.svg";
import DeleteModal from "../common/DeleteModal";

interface AddSearchDropdownProps {
  value: string | number;
  onChange: (event: SelectChangeEvent<string | number>) => void;
  options: { label: string; value: number | string }[];
  label?: string;
  placeHolder?: string;
  required?: boolean;
  error?: string;
  onUpdateTypeList: (
    updatedList: { label: string; value: string | number }[]
  ) => void;
  buttonSection?: boolean;
}

const AddSearchDropdown: React.FC<AddSearchDropdownProps> = ({
  value,
  onChange,
  options,
  label,
  placeHolder,
  required,
  error,
  onUpdateTypeList,
  buttonSection = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [newType, setNewType] = useState("");
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [typeList, setTypeList] = useState(options);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  useEffect(() => {
    setTypeList(options);
  }, [options]);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (originalIndex: number) => {
    setNewType(typeList[originalIndex].label);
    setEditIndex(originalIndex);
  };

  const handleAddOrUpdateType = () => {
    if (newType.trim()) {
      const lowerNewType = newType.toLowerCase();
      let updatedList = [...typeList];

      const isDuplicate = typeList.some(
        (item) => item.label.toLowerCase() === lowerNewType
      );

      if (editIndex !== null) {
        if (!isDuplicate) {
          updatedList[editIndex] = { label: newType, value: newType };
          setEditIndex(null);
        } else {
          showToast("Type already exists", "error");
        }
      } else {
        if (!isDuplicate) {
          updatedList = [...typeList, { label: newType, value: newType }];
        } else {
          showToast("Type already exists", "error");
        }
      }

      setTypeList(updatedList);
      onUpdateTypeList(updatedList);
      setNewType("");
    }
  };

  const handleSelect = (selectedValue: string | number) => {
    onChange({ target: { value: selectedValue } } as SelectChangeEvent<
      string | number
    >);
    handleClose();
  };
  const handleDeleteType = (index: number) => {
    setDeleteIndex(index);
    setIsDeleteModalOpen(true);
  };
  const handleDeleteData = () => {
    if (deleteIndex !== null) {
      const updatedList = [...typeList];
      updatedList.splice(deleteIndex, 1);
      setTypeList(updatedList);
      onUpdateTypeList(updatedList);
      setDeleteIndex(null);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        {label && (
          <Typography variant="subtitle1" sx={{ fontSize: "12px" }}>
            {label}{" "}
            {required && (
              <span style={{ color: "red", lineHeight: "1.5" }}>*</span>
            )}
          </Typography>
        )}

        <TextField
          fullWidth
          variant="outlined"
          value={typeList.find((option) => option.value === value)?.label || ""}
          placeholder={placeHolder}
          onClick={handleOpen}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ color: "#CCCCCC" }}>
                <ExpandMoreIcon sx={{ color: "#CCCCCC" }} />
              </InputAdornment>
            ),
            readOnly: true,
          }}
          sx={{
            cursor: "pointer",
            borderRadius: "0px",
            height: "40px",
            color: "#808080!important",
            backgroundColor: "#393939",
            "& .MuiOutlinedInput-root": {
              borderColor: error ? "#d32f2f!important" : "#808080!important",
              borderRadius: "0px",
              color: "#808080!important",
              opacity: 1,
            },
            "& .MuiInputBase-input": {
              color: "#CCCCCC",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#CCCCCC!important",
              opacity: 1,
            },
            "& input::placeholder": {
              color: "#CCCCCC!important",
              opacity: 1,
            },
            "& fieldset": {
              borderColor: error ? "#d32f2f !important" : "#808080",
            },
            "&:hover fieldset": {
              borderColor: "#808080 !important",
            },
          }}
        />

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{ mt: 0.5 }}
        >
          <Box sx={{ width: 340, borderRadius: "0px", background: "#212121" }}>
            <Box
              sx={{
                display: "flex",
                borderRadius: "0px",
                p: 1,
                background: "#212121",
                color: "#cccccc",
              }}
            >
              <TextField
                variant="outlined"
                size="small"
                placeholder="Add New Type"
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                sx={{
                  flex: 1,
                  borderRadius: "0px",
                  height: "40px",
                  background: "#212121",
                  color: "#cccccc",
                  border: "1px solid #808080",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px",
                    height: "40px",
                    color: "#cccccc",
                  },
                  "& input": {
                    color: "#CCCCCC !important",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#CCCCCC !important",
                    opacity: 1,
                  },
                  "& input::placeholder": {
                    color: "#CCCCCC !important",
                    opacity: 1,
                  },
                }}
              />
              <Button
                variant="contained"
                sx={{
                  borderRadius: "0px",
                  height: "41px",
                  boxShadow: "none",
                  background: "#DA291C",
                }}
                onClick={handleAddOrUpdateType}
              >
                {editIndex !== null ? "Update" : "Add"}
              </Button>
            </Box>

            <Box p={1} py={0} pt={1} sx={{ background: "#393939" }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search Type"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  width: "100%",
                  borderRadius: "0px",
                  border: "1px solid #808080",

                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px",
                    height: "40px",
                    color: "#ccccc",
                  },
                  "& input": {
                    color: "#CCCCCC !important",
                  },
                  "& input::placeholder": {
                    color: "#CCCCCC",
                    opacity: 1,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon fontSize="small" sx={{ color: "#cccccc" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <List
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                p: 1,
                background: "#393939",
              }}
            >
              {typeList
                .map((option, originalIndex) => ({ option, originalIndex }))
                .filter(({ option }) =>
                  option.label
                    ?.toLowerCase()
                    .includes(searchTerm?.toLowerCase())
                )
                .map(({ option, originalIndex }) => (
                  <ListItem
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    sx={{
                      display: "flex",
                      border: "1px solid #808080",
                      justifyContent: "space-between",
                      paddingRight: "8px",
                      bgcolor: "#393939",
                      color: "#cccccc",
                      "& input": {
                        color: "#CCCCCC !important",
                      },
                      "&:hover": { backgroundColor: "#393939" },
                    }}
                  >
                    <ListItemText primary={option.label} />
                    {buttonSection && (
                      <>
                        <IconButton
                          size="small"
                          style={{ padding: "0px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(originalIndex);
                          }}
                        >
                          <img
                            src={EditImage}
                            alt="Edit"
                            style={{ cursor: "pointer" }}
                            width={20}
                            height={20}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteType(originalIndex);
                          }}
                        >
                          {" "}
                          <img
                            src={DeleteIcon}
                            alt="Delete"
                            style={{ cursor: "pointer" }}
                            width={20}
                            height={20}
                          />
                        </IconButton>
                      </>
                    )}
                  </ListItem>
                ))}
            </List>
          </Box>
        </Popover>

        {error && (
          <Typography className="f-12" sx={{ color: "#d32f2f" }}>
            {error}
          </Typography>
        )}
      </Box>
      <DeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onDelete={handleDeleteData}
        title="Delete Type"
        description="Are you sure you want to delete this type?"
      />
    </>
  );
};

export default AddSearchDropdown;
